import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const BASE = () => (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

async function chamar(path: string, init?: { method?: string; body?: unknown }) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada — faça login novamente');
  const res = await fetch(`${BASE()}${path}`, {
    method: init?.method ?? 'GET',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `Erro ${res.status}`);
  return json?.data;
}

export interface PlanoClaroMovel { plano: string; gb: number; linhas: number }
export interface LinhaSemFatura {
  msisdn: string; plano: string; gb: number; valor: number;
  data_fidelidade: string | null; incluida_em: string; rotulo: string | null; na_fatura: boolean;
}

export function usePlanosClaroMovel(enabled: boolean) {
  return useQuery({
    queryKey: ['linha-sem-fatura', 'planos'],
    enabled,
    staleTime: 60 * 60_000,
    queryFn: async (): Promise<PlanoClaroMovel[]> => (await chamar('/v1/linha-sem-fatura/planos')) || [],
  });
}

export function useLinhasSemFatura(cnpj: string, enabled: boolean) {
  return useQuery({
    queryKey: ['linha-sem-fatura', 'linhas', cnpj],
    enabled: enabled && cnpj.length === 14,
    staleTime: 15_000,
    queryFn: async (): Promise<LinhaSemFatura[]> => (await chamar(`/v1/linha-sem-fatura/${cnpj}`)) || [],
  });
}

export function useLinhaSemFaturaAcoes(cnpj: string) {
  const qc = useQueryClient();
  const atualizar = () => {
    qc.invalidateQueries({ queryKey: ['linha-sem-fatura', 'linhas', cnpj] });
    qc.invalidateQueries({ queryKey: ['fatura-resumo-lead'] });
  };
  const incluir = useMutation({
    mutationFn: (v: { msisdn: string; plano: string; valor: number; tm: number }) =>
      chamar('/v1/linha-sem-fatura', { method: 'POST', body: { cnpj, ...v } }),
    onSuccess: atualizar,
  });
  const excluir = useMutation({
    mutationFn: (msisdn: string) => chamar(`/v1/linha-sem-fatura/${cnpj}/${msisdn}`, { method: 'DELETE' }),
    onSuccess: atualizar,
  });
  return { incluir, excluir };
}
